import { ArrowCounterClockwise, DownloadSimple, ImageSquare, Scan, SpinnerGap, UploadSimple } from "@phosphor-icons/react";
import { type PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from "react";

type HolderGrowth = {
  total?: number;
  daily?: { added: number; percent: number } | null;
  weekly?: { added: number; percent: number } | null;
};

type PhotoState = {
  x: number;
  y: number;
  scale: number;
  rotation: number;
  perspective: number;
  eyeGap: number;
  noseDx: number;
  noseDy: number;
  corners: [Point, Point, Point, Point];
};
type Point = { x: number; y: number };
type Gesture =
  | { kind: "drag"; origin: Point; base: PhotoState }
  | { kind: "pinch"; center: Point; distance: number; angle: number; base: PhotoState }
  | { kind: "warp"; corner: number; origin: Point; base: PhotoState };

const INITIAL: PhotoState = {
  x: 50,
  y: 45,
  scale: 52,
  rotation: 0,
  perspective: 0,
  eyeGap: .17,
  noseDx: 0,
  noseDy: .15,
  corners: [{ x: 0, y: 0 }, { x: 0, y: 0 }, { x: 0, y: 0 }, { x: 0, y: 0 }],
};
// Landmark locations measured from the transparent wearable-bag asset.
const BAG_EYE_X = .458;
const BAG_EYE_Y = .531;
const BAG_EYE_SPAN = .288;

function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(maximum, Math.max(minimum, value));
}

let detectorPromise: Promise<import("@mediapipe/tasks-vision").FaceLandmarker> | null = null;

async function getFaceDetector() {
  if (!detectorPromise) {
    detectorPromise = import("@mediapipe/tasks-vision").then(async ({ FaceLandmarker, FilesetResolver }) => {
      const vision = await FilesetResolver.forVisionTasks("/wasm");
      const options = {
        baseOptions: { modelAssetPath: "/models/face_landmarker.task", delegate: "GPU" as const },
        runningMode: "IMAGE" as const,
        numFaces: 1,
        minFaceDetectionConfidence: .55,
        minFacePresenceConfidence: .55,
        minTrackingConfidence: .5,
        outputFacialTransformationMatrixes: true,
      };
      try {
        return await FaceLandmarker.createFromOptions(vision, options);
      } catch {
        return FaceLandmarker.createFromOptions(vision, {
          ...options,
          baseOptions: { modelAssetPath: "/models/face_landmarker.task", delegate: "CPU" },
        });
      }
    });
  }
  return detectorPromise;
}

function mix(a: Point, b: Point, amount: number): Point {
  return { x: a.x + (b.x - a.x) * amount, y: a.y + (b.y - a.y) * amount };
}

function quadPoint(quad: [Point, Point, Point, Point], u: number, v: number): Point {
  return mix(mix(quad[0], quad[1], u), mix(quad[3], quad[2], u), v);
}

function drawTriangle(
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  source: [Point, Point, Point],
  target: [Point, Point, Point],
) {
  const [s0, s1, s2] = source;
  const [d0, d1, d2] = target;
  const determinant = s0.x * (s1.y - s2.y) + s1.x * (s2.y - s0.y) + s2.x * (s0.y - s1.y);
  if (Math.abs(determinant) < .001) return;
  const a = (d0.x * (s1.y - s2.y) + d1.x * (s2.y - s0.y) + d2.x * (s0.y - s1.y)) / determinant;
  const b = (d0.y * (s1.y - s2.y) + d1.y * (s2.y - s0.y) + d2.y * (s0.y - s1.y)) / determinant;
  const c = (d0.x * (s2.x - s1.x) + d1.x * (s0.x - s2.x) + d2.x * (s1.x - s0.x)) / determinant;
  const d = (d0.y * (s2.x - s1.x) + d1.y * (s0.x - s2.x) + d2.y * (s1.x - s0.x)) / determinant;
  const e = (d0.x * (s1.x * s2.y - s2.x * s1.y) + d1.x * (s2.x * s0.y - s0.x * s2.y) + d2.x * (s0.x * s1.y - s1.x * s0.y)) / determinant;
  const f = (d0.y * (s1.x * s2.y - s2.x * s1.y) + d1.y * (s2.x * s0.y - s0.x * s2.y) + d2.y * (s0.x * s1.y - s1.x * s0.y)) / determinant;
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(d0.x, d0.y);
  ctx.lineTo(d1.x, d1.y);
  ctx.lineTo(d2.x, d2.y);
  ctx.closePath();
  ctx.clip();
  ctx.transform(a, b, c, d, e, f);
  ctx.drawImage(image, 0, 0);
  ctx.restore();
}

function drawWarpedImage(ctx: CanvasRenderingContext2D, image: HTMLImageElement, quad: [Point, Point, Point, Point]) {
  const columns = 8;
  const rows = 8;
  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      const u0 = column / columns;
      const u1 = (column + 1) / columns;
      const v0 = row / rows;
      const v1 = (row + 1) / rows;
      const s00 = { x: u0 * image.width, y: v0 * image.height };
      const s10 = { x: u1 * image.width, y: v0 * image.height };
      const s11 = { x: u1 * image.width, y: v1 * image.height };
      const s01 = { x: u0 * image.width, y: v1 * image.height };
      const d00 = quadPoint(quad, u0, v0);
      const d10 = quadPoint(quad, u1, v0);
      const d11 = quadPoint(quad, u1, v1);
      const d01 = quadPoint(quad, u0, v1);
      drawTriangle(ctx, image, [s00, s10, s11], [d00, d10, d11]);
      drawTriangle(ctx, image, [s00, s11, s01], [d00, d11, d01]);
    }
  }
}

function holePath(ctx: CanvasRenderingContext2D, center: Point, radiusX: number, radiusY: number, rotation: number, seed: number, inset = 1) {
  const points = 22;
  ctx.beginPath();
  for (let index = 0; index <= points; index += 1) {
    const angle = index / points * Math.PI * 2;
    const roughness = 1 + Math.sin(index * 4.73 + seed) * .055 + Math.sin(index * 7.19 + seed * 2) * .025;
    const localX = Math.cos(angle) * radiusX * roughness * inset;
    const localY = Math.sin(angle) * radiusY * roughness * inset;
    const x = center.x + localX * Math.cos(rotation) - localY * Math.sin(rotation);
    const y = center.y + localX * Math.sin(rotation) + localY * Math.cos(rotation);
    if (index === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

function cutTornHole(ctx: CanvasRenderingContext2D, center: Point, radiusX: number, radiusY: number, rotation: number, seed: number) {
  ctx.save();
  ctx.globalCompositeOperation = "source-over";
  ctx.fillStyle = "rgba(66, 37, 18, .58)";
  holePath(ctx, center, radiusX, radiusY, rotation, seed, 1.13);
  ctx.fill();
  ctx.globalCompositeOperation = "destination-out";
  holePath(ctx, center, radiusX, radiusY, rotation, seed, .92);
  ctx.fill();
  ctx.restore();
}

function bagQuad(node: { width: number; height: number }, position: PhotoState): [Point, Point, Point, Point] {
  const size = node.width * position.scale / 100;
  const anchor = { x: node.width * position.x / 100, y: node.height * position.y / 100 };
  const radians = position.rotation * Math.PI / 180;
  const cosine = Math.cos(radians);
  const sine = Math.sin(radians);
  const local: [Point, Point, Point, Point] = [
    { x: -BAG_EYE_X * size, y: -BAG_EYE_Y * size },
    { x: (1 - BAG_EYE_X) * size, y: -BAG_EYE_Y * size },
    { x: (1 - BAG_EYE_X) * size, y: (1 - BAG_EYE_Y) * size },
    { x: -BAG_EYE_X * size, y: (1 - BAG_EYE_Y) * size },
  ];
  const yaw = position.perspective;
  const compression = Math.abs(yaw) * size * .22;
  if (yaw > 0) {
    local[0].x += compression;
    local[3].x += compression * .72;
    local[0].y += compression * .08;
  } else if (yaw < 0) {
    local[1].x -= compression;
    local[2].x -= compression * .72;
    local[1].y += compression * .08;
  }
  local.forEach((point, index) => {
    point.x += position.corners[index].x * size;
    point.y += position.corners[index].y * size;
  });
  return local.map(({ x, y }) => ({
    x: anchor.x + x * cosine - y * sine,
    y: anchor.y + x * sine + y * cosine,
  })) as [Point, Point, Point, Point];
}

function signed(value: number) {
  return `${value >= 0 ? "+" : ""}${value.toLocaleString()}`;
}

function percentage(value: number) {
  return `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;
}

export function BagMaker({ holderGrowth }: { holderGrowth?: HolderGrowth }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const pointers = useRef(new Map<number, Point>());
  const gesture = useRef<Gesture | null>(null);
  const positionRef = useRef(INITIAL);
  const [photo, setPhoto] = useState<HTMLImageElement | null>(null);
  const [bag, setBag] = useState<HTMLImageElement | null>(null);
  const [position, setPosition] = useState(INITIAL);
  const [paperTone, setPaperTone] = useState(1);
  const [detection, setDetection] = useState<"idle" | "scanning" | "found" | "manual">("idle");
  const [warpMode, setWarpMode] = useState(false);

  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  useEffect(() => {
    const image = new Image();
    image.src = "/assets/paper-bag-blank-v3.png";
    image.onload = () => setBag(image);
  }, []);

  useEffect(() => {
    const node = canvas.current;
    if (!node) return;
    const ctx = node.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, node.width, node.height);
    ctx.fillStyle = "#171714";
    ctx.fillRect(0, 0, node.width, node.height);
    if (!photo) {
      ctx.fillStyle = "#9b9b8f";
      ctx.font = "500 28px Space Grotesk, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("YOUR FACE GOES HERE", node.width / 2, node.height / 2);
      return;
    }
    const ratio = Math.max(node.width / photo.width, node.height / photo.height);
    const width = photo.width * ratio;
    const height = photo.height * ratio;
    ctx.drawImage(photo, (node.width - width) / 2, (node.height - height) / 2, width, height);
    if (bag) {
      const size = node.width * position.scale / 100;
      const quad = bagQuad(node, position);
      const leftEye = quadPoint(quad, BAG_EYE_X - position.eyeGap / 2, BAG_EYE_Y);
      const rightEye = quadPoint(quad, BAG_EYE_X + position.eyeGap / 2, BAG_EYE_Y);
      const nose = quadPoint(quad, BAG_EYE_X + position.noseDx, BAG_EYE_Y + position.noseDy);
      const eyeRadiusX = size * Math.min(.076, Math.max(.05, position.eyeGap * .36));
      const eyeRadiusY = eyeRadiusX * .78;
      const noseRadius = size * Math.min(.047, Math.max(.032, position.noseDy * .25));
      const holeRotation = position.rotation * Math.PI / 180;

      // Darkened openings place the photographed features behind the paper.
      ctx.save();
      ctx.filter = `blur(${Math.max(2, size * .01)}px)`;
      ctx.fillStyle = "rgba(0, 0, 0, .07)";
      for (const point of [leftEye, rightEye]) {
        ctx.beginPath();
        ctx.ellipse(point.x, point.y, eyeRadiusX * 1.05, eyeRadiusY * 1.05, holeRotation, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.beginPath();
      ctx.ellipse(nose.x, nose.y, noseRadius * 1.05, noseRadius * 1.1, holeRotation, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // A contact shadow behind the lower edge makes the bag meet the neck.
      const lowerCenter = quadPoint(quad, .5, .985);
      ctx.save();
      ctx.translate(lowerCenter.x, lowerCenter.y + size * .018);
      ctx.rotate(position.rotation * Math.PI / 180);
      ctx.filter = `blur(${Math.max(5, size * .025)}px)`;
      ctx.fillStyle = "rgba(0, 0, 0, .26)";
      ctx.beginPath();
      ctx.ellipse(0, 0, size * .27, size * .028, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      const layer = document.createElement("canvas");
      layer.width = node.width;
      layer.height = node.height;
      const layerContext = layer.getContext("2d");
      if (layerContext) {
        drawWarpedImage(layerContext, bag, quad);
        cutTornHole(layerContext, leftEye, eyeRadiusX, eyeRadiusY, holeRotation, 1.7);
        cutTornHole(layerContext, rightEye, eyeRadiusX, eyeRadiusY, holeRotation, 3.1);
        cutTornHole(layerContext, nose, noseRadius, noseRadius * 1.04, holeRotation, 5.3);
      }

      ctx.save();
      ctx.filter = `brightness(${paperTone}) saturate(.88) contrast(1.04) drop-shadow(0 ${Math.max(4, size * .018)}px ${Math.max(8, size * .04)}px rgba(0,0,0,.48))`;
      ctx.drawImage(layer, 0, 0);
      ctx.restore();
    }
  }, [photo, bag, position, paperTone]);

  const fitBagToFace = async (image: HTMLImageElement) => {
    setDetection("scanning");
    try {
      const detector = await getFaceDetector();
      const landmarks = detector.detect(image).faceLandmarks[0];
      if (!landmarks?.[454]) throw new Error("No face found");
      const average = (indices: number[]) => ({
        x: indices.reduce((sum, index) => sum + landmarks[index].x, 0) / indices.length * image.width,
        y: indices.reduce((sum, index) => sum + landmarks[index].y, 0) / indices.length * image.height,
      });
      const eyes = [average([33, 133, 159, 145]), average([362, 263, 386, 374])].sort((a, b) => a.x - b.x);
      const eyeCenterX = (eyes[0].x + eyes[1].x) / 2;
      const eyeCenterY = (eyes[0].y + eyes[1].y) / 2;
      const eyeDistance = Math.hypot(eyes[1].x - eyes[0].x, eyes[1].y - eyes[0].y);
      const nose = { x: landmarks[1].x * image.width, y: landmarks[1].y * image.height };
      const cheekWidth = Math.hypot(
        (landmarks[454].x - landmarks[234].x) * image.width,
        (landmarks[454].y - landmarks[234].y) * image.height,
      );
      const faceHeight = Math.hypot(
        (landmarks[152].x - landmarks[10].x) * image.width,
        (landmarks[152].y - landmarks[10].y) * image.height,
      );
      const angle = Math.atan2(eyes[1].y - eyes[0].y, eyes[1].x - eyes[0].x) * 180 / Math.PI;
      const node = canvas.current!;
      const ratio = Math.max(node.width / image.width, node.height / image.height);
      const offsetX = (node.width - image.width * ratio) / 2;
      const offsetY = (node.height - image.height * ratio) / 2;
      const sizeFromEyes = eyeDistance / BAG_EYE_SPAN;
      const eyeToNose = Math.hypot(nose.x - eyeCenterX, nose.y - eyeCenterY);
      const sizeFromNose = eyeToNose / .156;
      // Face mesh hugs skin and excludes hair/head depth. Expand the solved
      // envelope so the paper reaches above the hair and down to the neck.
      const fittedHeadSize = Math.max((sizeFromEyes + sizeFromNose) / 2, cheekWidth * 1.48, faceHeight * 1.18) * 1.38;
      const size = fittedHeadSize * ratio;
      const yaw = Math.min(.34, Math.max(-.34, (nose.x - eyeCenterX) / Math.max(eyeDistance, 1) * 1.7));
      const inverseRoll = -angle * Math.PI / 180;
      const noseOffsetX = nose.x - eyeCenterX;
      const noseOffsetY = nose.y - eyeCenterY;
      const localNoseX = noseOffsetX * Math.cos(inverseRoll) - noseOffsetY * Math.sin(inverseRoll);
      const localNoseY = noseOffsetX * Math.sin(inverseRoll) + noseOffsetY * Math.cos(inverseRoll);

      const sampler = document.createElement("canvas");
      sampler.width = 32;
      sampler.height = 32;
      const sampleContext = sampler.getContext("2d", { willReadFrequently: true });
      if (sampleContext) {
        sampleContext.drawImage(image, 0, 0, 32, 32);
        const pixels = sampleContext.getImageData(0, 0, 32, 32).data;
        let luminance = 0;
        for (let index = 0; index < pixels.length; index += 4) {
          luminance += pixels[index] * .2126 + pixels[index + 1] * .7152 + pixels[index + 2] * .0722;
        }
        const averageLuminance = luminance / (pixels.length / 4) / 255;
        setPaperTone(Math.min(1.12, Math.max(.68, .62 + averageLuminance * .62)));
      }
      setPosition({
        x: Math.min(90, Math.max(10, (offsetX + eyeCenterX * ratio) / node.width * 100)),
        y: Math.min(90, Math.max(10, (offsetY + eyeCenterY * ratio) / node.height * 100)),
        scale: Math.min(95, Math.max(25, size / node.width * 100)),
        rotation: Math.min(24, Math.max(-24, angle)),
        perspective: yaw,
        eyeGap: Math.min(.28, Math.max(.10, eyeDistance / fittedHeadSize)),
        noseDx: Math.min(.12, Math.max(-.12, localNoseX / fittedHeadSize)),
        noseDy: Math.min(.25, Math.max(.08, localNoseY / fittedHeadSize)),
        corners: INITIAL.corners.map((corner) => ({ ...corner })) as PhotoState["corners"],
      });
      setDetection("found");
    } catch (error) {
      console.warn("Automatic face fit unavailable", error);
      setPosition(INITIAL);
      setDetection("manual");
    }
  };

  const upload = (file?: File) => {
    if (!file || !file.type.startsWith("image/")) return;
    setWarpMode(false);
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => { setPhoto(image); void fitBagToFace(image); URL.revokeObjectURL(url); };
    image.src = url;
  };

  const download = () => {
    if (!photo || !canvas.current) return;
    const link = document.createElement("a");
    link.download = "the-bag-stays-on.png";
    link.href = canvas.current.toDataURL("image/png");
    link.click();
  };

  const beginGesture = () => {
    if (warpMode) {
      gesture.current = null;
      return;
    }
    const points = [...pointers.current.values()];
    if (points.length >= 2) {
      const [first, second] = points;
      gesture.current = {
        kind: "pinch",
        center: { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 },
        distance: Math.max(1, Math.hypot(second.x - first.x, second.y - first.y)),
        angle: Math.atan2(second.y - first.y, second.x - first.x),
        base: positionRef.current,
      };
    } else if (points.length === 1) {
      gesture.current = { kind: "drag", origin: points[0], base: positionRef.current };
    } else {
      gesture.current = null;
    }
  };

  const pointerDown = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!photo) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (warpMode) {
      const rect = event.currentTarget.getBoundingClientRect();
      const point = {
        x: (event.clientX - rect.left) / rect.width * event.currentTarget.width,
        y: (event.clientY - rect.top) / rect.height * event.currentTarget.height,
      };
      const quad = bagQuad(event.currentTarget, positionRef.current);
      const corner = quad.reduce((nearest, candidate, index) => {
        const distance = Math.hypot(candidate.x - point.x, candidate.y - point.y);
        return distance < nearest.distance ? { index, distance } : nearest;
      }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;
      gesture.current = { kind: "warp", corner, origin: { x: event.clientX, y: event.clientY }, base: positionRef.current };
      return;
    }
    beginGesture();
  };

  const pointerMove = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!photo || !pointers.current.has(event.pointerId) || !gesture.current) return;
    event.preventDefault();
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const points = [...pointers.current.values()];
    const rect = event.currentTarget.getBoundingClientRect();
    const activeGesture = gesture.current;
    let next: PhotoState;

    if (activeGesture.kind === "warp" && points.length === 1) {
      const size = event.currentTarget.width * activeGesture.base.scale / 100;
      const deltaX = (points[0].x - activeGesture.origin.x) / rect.width * event.currentTarget.width;
      const deltaY = (points[0].y - activeGesture.origin.y) / rect.height * event.currentTarget.height;
      const radians = activeGesture.base.rotation * Math.PI / 180;
      const localX = (deltaX * Math.cos(radians) + deltaY * Math.sin(radians)) / size;
      const localY = (-deltaX * Math.sin(radians) + deltaY * Math.cos(radians)) / size;
      const corners = activeGesture.base.corners.map((corner) => ({ ...corner })) as PhotoState["corners"];
      corners[activeGesture.corner] = {
        x: clamp(activeGesture.base.corners[activeGesture.corner].x + localX, -.3, .3),
        y: clamp(activeGesture.base.corners[activeGesture.corner].y + localY, -.3, .3),
      };
      next = { ...activeGesture.base, corners };
    } else if (points.length >= 2 && activeGesture.kind === "pinch") {
      const [first, second] = points;
      const center = { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 };
      const distance = Math.max(1, Math.hypot(second.x - first.x, second.y - first.y));
      const angle = Math.atan2(second.y - first.y, second.x - first.x);
      let rotationDelta = (angle - activeGesture.angle) * 180 / Math.PI;
      if (rotationDelta > 180) rotationDelta -= 360;
      if (rotationDelta < -180) rotationDelta += 360;
      next = {
        ...activeGesture.base,
        x: clamp(activeGesture.base.x + (center.x - activeGesture.center.x) / rect.width * 100, 5, 95),
        y: clamp(activeGesture.base.y + (center.y - activeGesture.center.y) / rect.height * 100, 5, 95),
        scale: clamp(activeGesture.base.scale * distance / activeGesture.distance, 20, 110),
        rotation: clamp(activeGesture.base.rotation + rotationDelta, -45, 45),
      };
    } else if (points.length === 1 && activeGesture.kind === "drag") {
      next = {
        ...activeGesture.base,
        x: clamp(activeGesture.base.x + (points[0].x - activeGesture.origin.x) / rect.width * 100, 5, 95),
        y: clamp(activeGesture.base.y + (points[0].y - activeGesture.origin.y) / rect.height * 100, 5, 95),
      };
    } else {
      beginGesture();
      return;
    }
    positionRef.current = next;
    setPosition(next);
  };

  const pointerUp = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    pointers.current.delete(event.pointerId);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    beginGesture();
  };

  const controlValue = (key: "x" | "y" | "scale" | "rotation" | "perspective") => {
    if (key === "rotation") return `${Math.round(position[key])}°`;
    if (key === "perspective") return `${Math.round(position[key] * 100)}`;
    return `${Math.round(position[key])}%`;
  };

  const editorQuad = photo ? bagQuad({ width: 900, height: 900 }, position) : null;

  return (
    <section className="maker section" id="maker" aria-labelledby="maker-title">
      <div className="maker-copy">
        <p className="eyebrow">Become anonymous</p>
        <h2 id="maker-title">Bag your profile.</h2>
        <p>Upload a photo. We’ll fit and warp the paper around your head automatically; the controls are there for a final nudge. Everything runs in your browser. Your image is never uploaded.</p>
        <div className="holder-growth" aria-label="Live ZCAT holder growth">
          <p>How many people have put the bag on?</p>
          <div>
            <span><i>24H added</i><strong>{holderGrowth?.daily ? signed(holderGrowth.daily.added) : "—"}</strong><small>{holderGrowth?.daily ? percentage(holderGrowth.daily.percent) : "Syncing"}</small></span>
            <span><i>7D added</i><strong>{holderGrowth?.weekly ? signed(holderGrowth.weekly.added) : "—"}</strong><small>{holderGrowth?.weekly ? percentage(holderGrowth.weekly.percent) : "Building history"}</small></span>
            <span><i>Total holders</i><strong>{holderGrowth?.total?.toLocaleString() ?? "—"}</strong><small>Live count</small></span>
          </div>
        </div>
        <label className="button button-primary upload-button">
          <UploadSimple size={19} weight="bold" /> Choose photo
          <input type="file" accept="image/*" onChange={(event) => upload(event.target.files?.[0])} />
        </label>
        <div className={`detection-line ${detection}`}>
          {detection === "scanning" ? <SpinnerGap className="spin" size={18} /> : detection === "found" ? <Scan size={18} weight="bold" /> : <ImageSquare size={18} />}
          {detection === "scanning" ? "Finding your eyes on-device..." : detection === "found" ? "Face found. Bag auto-fitted." : detection === "manual" ? "No clear face found. Manual controls ready." : "Local-only image processing"}
        </div>
      </div>
      <div className="maker-studio">
        <div className="maker-preview">
          <canvas
            ref={canvas}
            width="900"
            height="900"
            className={photo ? `interactive${warpMode ? " warping" : ""}` : ""}
            aria-label="Profile image preview with draggable paper bag overlay"
            onPointerDown={pointerDown}
            onPointerMove={pointerMove}
            onPointerUp={pointerUp}
            onPointerCancel={pointerUp}
          />
          {warpMode && editorQuad && (
            <svg className="warp-guides" viewBox="0 0 900 900" aria-hidden="true">
              <polygon points={editorQuad.map((point) => `${point.x},${point.y}`).join(" ")} />
              {editorQuad.map((point, index) => <circle key={index} cx={point.x} cy={point.y} r="18" />)}
            </svg>
          )}
          {photo && <p className="gesture-hint">{warpMode ? "Drag any green corner to shape the bag" : "Drag to move · Pinch to resize · Twist to rotate"}</p>}
        </div>
        <div className="maker-controls">
          {(["x", "y", "scale", "rotation", "perspective"] as const).map((key) => (
            <label key={key}>
              <span className="control-label"><span>{key === "x" ? "Left / right" : key === "y" ? "Up / down" : key === "perspective" ? "Face angle" : key}</span><output>{controlValue(key)}</output></span>
              <input type="range" min={key === "perspective" ? -.35 : key === "rotation" ? -45 : key === "scale" ? 20 : 5} max={key === "perspective" ? .35 : key === "rotation" ? 45 : key === "scale" ? 110 : 95} step={key === "perspective" ? .01 : 1} value={position[key]} aria-valuetext={controlValue(key)} onChange={(event) => setPosition({ ...position, [key]: Number(event.target.value) })} disabled={!photo} />
            </label>
          ))}
          <div className="maker-actions">
            <button className={`button button-quiet${warpMode ? " active" : ""}`} aria-pressed={warpMode} onClick={() => setWarpMode((active) => !active)} disabled={!photo}><Scan size={18} /> {warpMode ? "Finish fine warp" : "Fine-warp corners"}</button>
            <button className="button button-quiet" onClick={() => setPosition(INITIAL)} disabled={!photo}><ArrowCounterClockwise size={18} /> Reset</button>
            <button className="button button-primary" onClick={download} disabled={!photo}><DownloadSimple size={18} weight="bold" /> Download PFP</button>
          </div>
        </div>
      </div>
    </section>
  );
}
